import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, FileText, AlertCircle, Calendar, Clock, Shield, DollarSign, Package, Receipt, Loader, Building, CheckCircle, MapPin, Paperclip, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Import the SHARED Firebase Invoice Service
import {
  addInvoice,
  generateInvoiceNumber,
  setCompanyContext,
  db
} from '../services/firebaseInvoiceService';

// Custom motorbike icon component
const MotorbikeIcon = ({ size = 16, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8 5h8l2 4h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1a3 3 0 0 1-3 3 3 3 0 0 1-3-3H7a3 3 0 0 1-3 3 3 3 0 0 1-3-3V7a2 2 0 0 1 2-2h5zm0 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm8 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
  </svg>
);

// ============================================================================
// ✅ NEW: DOCUMENT HELPERS
// ============================================================================

// Turns reservation.uploadedDocuments (an object keyed by document id)
// into an array of documents that actually have a download link.
const getUploadedDocuments = (reservation) => {
  const uploaded = reservation?.uploadedDocuments;
  if (!uploaded || typeof uploaded !== 'object') return [];

  return Object.entries(uploaded)
    .filter(([, docData]) => docData && docData.downloadURL)
    .map(([docId, docData]) => ({
      id: docId,
      name: docData.documentName || docData.fileName || `Document ${docId}`,
      fileName: docData.fileName || '',
      fileType: docData.fileType || '',
      downloadURL: docData.downloadURL
    }));
};

// Older reservations may not have fileType saved, so also check the file extension
const isImageDocument = (document) => {
  if (document.fileType && document.fileType.startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp|heic)$/i.test(document.fileName || '');
};

// Required documents (from the settings saved with the reservation) that have no file
const getMissingRequiredDocuments = (reservation) => {
  const requirements = Array.isArray(reservation?.documentRequirements)
    ? reservation.documentRequirements
    : [];
  const uploaded = reservation?.uploadedDocuments || {};

  return requirements.filter(req =>
    req && req.required === true && !(uploaded[req.id] && uploaded[req.id].downloadURL)
  );
};

// FETCH ACTUAL INVOICE FROM FIREBASE USING SHARED SERVICE
const fetchInvoiceForReservation = async (reservationId, companyId) => {
  if (!db || !companyId) {
    throw new Error('Firebase not available or company context missing');
  }

  console.log('🔍 Fetching invoice for reservation:', reservationId, 'in company:', companyId);

  // Use the SAME collection as firebaseInvoiceService
  const invoiceQuery = query(
    collection(db, 'invoices'),
    where('reservationId', '==', reservationId),
    where('companyId', '==', companyId)
  );
  
  const invoiceSnapshot = await getDocs(invoiceQuery);
  if (!invoiceSnapshot.empty) {
    const invoiceDoc = invoiceSnapshot.docs[0];
    console.log('✅ Found invoice:', invoiceDoc.data().invoiceNumber);
    return {
      id: invoiceDoc.id,
      ...invoiceDoc.data()
    };
  }
  
  console.log('⚠️ No invoice found for this reservation');
  return null;
};

// UNIFIED invoice creation using firebaseInvoiceService - WITH DELIVERY CHARGES
const createInvoiceFromReservation = async (reservation, currentUser, currentCompany, companyId) => {
  if (!reservation || !reservation.id) {
    throw new Error('Invalid reservation data');
  }

  if (!currentUser || !currentUser.id) {
    throw new Error('User information is required');
  }

  if (!currentCompany) {
    throw new Error('Company information is required');
  }

  if (!db || !companyId) {
    throw new Error('Firebase not available or company context missing. Cannot save invoice to database.');
  }

  console.log('🧾 Creating invoice using SHARED firebaseInvoiceService');

  // Set company context for the service
  setCompanyContext(companyId, currentCompany.name);

  // Check for duplicate invoice using SAME collection
  const duplicateQuery = query(
    collection(db, 'invoices'),
    where('reservationId', '==', reservation.id),
    where('companyId', '==', companyId)
  );
  
  const duplicateSnapshot = await getDocs(duplicateQuery);
  if (!duplicateSnapshot.empty) {
    throw new Error('An invoice already exists for this reservation');
  }

  // Generate invoice number using SHARED service
  const isExtended = reservation.isExtendedRental || reservation.type === 'extended';
  const invoiceNumber = isExtended 
    ? await generateInvoiceNumber(companyId)
    : await generateInvoiceNumber(companyId);
  
  console.log('✅ Generated invoice number:', invoiceNumber);
  
  // ✅✅✅ FIXED CALCULATION - INCLUDES DELIVERY CHARGES ✅✅✅
  const rentalAmount = parseFloat(reservation.rentalAmount) || 0;
  const addonCosts = parseFloat(reservation.addonCosts) || 0;
  const discountAmount = parseFloat(reservation.discountAmount) || 0;
  const deliveryCharges = parseFloat(reservation.deliveryCharges) || 0;  // ✅ READ delivery charges
  
  // ✅ CORRECT FORMULA: Rental + Addons + Delivery - Discount
  const subtotalAmount = rentalAmount + addonCosts + deliveryCharges;
  const totalAmount = Math.max(0, subtotalAmount - discountAmount);
  
  console.log('💰 ========================================');
  console.log('💰 INVOICE CALCULATION (ReservationList)');
  console.log('💰 ========================================');
  console.log('💰 Rental Amount:        $' + rentalAmount.toFixed(2));
  console.log('💰 Addon Costs:          $' + addonCosts.toFixed(2));
  console.log('💰 Delivery Charges:     $' + deliveryCharges.toFixed(2) + ' ✅');
  console.log('💰 ----------------------------------------');
  console.log('💰 Subtotal:             $' + subtotalAmount.toFixed(2));
  console.log('💰 Discount:             -$' + discountAmount.toFixed(2));
  console.log('💰 ----------------------------------------');
  console.log('💰 TOTAL AMOUNT:         $' + totalAmount.toFixed(2) + ' ✅');
  console.log('💰 ========================================');
  
  const invoiceData = {
    invoiceNumber,
    reservationId: reservation.id,
    reservationNumber: reservation.reservationNumber || '',
    originalReservationId: reservation.id,
    
    // Customer information
    customerName: reservation.customerName || 'Unknown Customer',
    customerEmail: reservation.email || '',
    customerPhone: reservation.whatsappNumber || '',
    email: reservation.email || '',
    whatsappNumber: reservation.whatsappNumber || '',
    licenseNumber: reservation.licenseNumber || '',
    
    // Vehicle information
    motorbikeNumber: reservation.motorbikeNumber || '',
    bikeModel: reservation.bikeModel || '',
    
    // ✅✅✅ DELIVERY CHARGES - COMPLETE SECTION ✅✅✅
    deliveryCharges: deliveryCharges.toString(),
    pickupLocation: reservation.pickupLocation || 'Not specified',
    dropoffLocation: reservation.dropoffLocation || 'Not specified',
    pickupLocationDetails: reservation.pickupLocationDetails || null,
    dropoffLocationDetails: reservation.dropoffLocationDetails || null,
    
    // Financial amounts - WITH DELIVERY CHARGES
    totalAmount: totalAmount,
    rentalAmount: rentalAmount,
    subtotalAmount: subtotalAmount,
    addonCosts: addonCosts,
    discountAmount: discountAmount,
    
    // Deposit information
    depositAmount: reservation.depositAmount || '0',
    depositData: reservation.depositData || null,
    depositCurrency: reservation.depositCurrency || null,
    depositSymbol: reservation.depositSymbol || null,
    
    // Rental dates
    pickupDateTime: reservation.pickupDateTime || '',
    dropoffDateTime: reservation.dropoffDateTime || '',
    
    // Add-ons
    selectedAddons: reservation.selectedAddons || [],
    
    // Discount information
    discountData: reservation.discountData || null,
    hasDiscount: discountAmount > 0,
    
    // Documents (if available)
    uploadedDocuments: reservation.uploadedDocuments || {},
    documentRequirements: reservation.documentRequirements || [],
    documentValidationStatus: reservation.documentValidationStatus || 'pending',
    documentsStoredInFirebase: reservation.documentsStoredInFirebase || false,
    documentCount: reservation.documentCount || 0,
    
    // Payment information
    paymentMethod: reservation.paymentMethod || 'Cash',
    rentalPaymentStatus: reservation.rentalPaymentStatus || 'pending',
    status: 'generated',
    note: reservation.note || '',
    
    // Invoice type
    isExtendedRental: isExtended,
    automaticallyGenerated: false,
    type: isExtended ? 'extended' : 'regular',
    
    // User tracking
    createdByUsername: currentUser.username || 'Unknown',
    createdBy: currentUser.id,
    sourceComponent: 'reservations',
    
    // Applied settings (if available)
    appliedSettings: reservation.appliedSettings || {},
    calculationDetails: reservation.calculationDetails || null
  };
  
  console.log('💾 Saving invoice to SHARED collection (invoices) with company context:', companyId);
  console.log('🚴 Invoice includes bike model:', invoiceData.bikeModel);
  console.log('🎫 Invoice includes reservation number:', invoiceData.reservationNumber);
  console.log('🗺️ Invoice includes delivery charges:', invoiceData.deliveryCharges);
  console.log('📍 Pickup location:', invoiceData.pickupLocation);
  console.log('📍 Dropoff location:', invoiceData.dropoffLocation);
  
  const invoiceId = await addInvoice(invoiceData, companyId, currentCompany.name);
  
  console.log('✅ Invoice created successfully with ID:', invoiceId);
  console.log('✅ Invoice will now appear in BOTH Invoice Management AND Finance Manager');
  console.log('✅ Total amount includes delivery charges: $' + totalAmount.toFixed(2));
  
  return {
    id: invoiceId,
    ...invoiceData,
    createdAt: new Date().toISOString()
  };
};

const ReservationList = ({ 
  reservations, 
  emptyMessage, 
  hoverColor, 
  iconColor,
  onExpandDetails = null,
  maxHeight = "500px",
  currentUser,
  currentCompany,
  showCompanyInfo = false,
  showInvoiceButton = true,
  onInvoiceCreated = null,
  existingInvoices = [],
  getCompanyContext = null,
  onNavigateToInvoicePreview = null
}) => {
  const [processingInvoices, setProcessingInvoices] = useState(new Set());
  const [createdInvoices, setCreatedInvoices] = useState(() => {
    const initialSet = new Set(existingInvoices);
    
    try {
      const stored = localStorage.getItem('createdInvoices');
      if (stored) {
        const storedIds = JSON.parse(stored);
        storedIds.forEach(id => initialSet.add(id));
      }
    } catch (error) {
      console.warn('Failed to load invoice state from localStorage:', error);
    }
    
    return initialSet;
  });

  const updateStoredInvoices = (newSet) => {
    try {
      localStorage.setItem('createdInvoices', JSON.stringify([...newSet]));
    } catch (error) {
      console.warn('Failed to save invoice state to localStorage:', error);
    }
  };

  useEffect(() => {
    if (existingInvoices && existingInvoices.length > 0) {
      setCreatedInvoices(prev => {
        const newSet = new Set([...prev, ...existingInvoices]);
        updateStoredInvoices(newSet);
        return newSet;
      });
    }
  }, [existingInvoices]);

  const scrollableContainerStyle = {
    maxHeight: maxHeight,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: '#4B5563 #1F2937',
  };

  const scrollbarCSS = `
    .reservation-scrollable::-webkit-scrollbar {
      width: 8px;
    }
    .reservation-scrollable::-webkit-scrollbar-track {
      background: #1F2937;
      border-radius: 4px;
    }
    .reservation-scrollable::-webkit-scrollbar-thumb {
      background: #4B5563;
      border-radius: 4px;
    }
    .reservation-scrollable::-webkit-scrollbar-thumb:hover {
      background: #6B7280;
    }
  `;

  const handleCreateInvoice = async (reservation) => {
    if (processingInvoices.has(reservation.id)) {
      alert('Invoice creation already in progress. Please wait...');
      return;
    }

    if (!currentUser || !currentCompany || !reservation.id) {
      alert('Missing required information. Please refresh and try again.');
      return;
    }

    if (reservation.availabilityStatus === 'cancel') {
      alert('Cannot create invoice for cancelled reservations.');
      return;
    }

    if (reservation.availabilityStatus !== 'booked') {
      alert('Cannot create invoice. Reservation must be "booked" status.\n\nCurrent status: ' + reservation.availabilityStatus);
      return;
    }

    if (!getCompanyContext) {
      alert('Company context not available. Please ensure the component is properly configured.');
      return;
    }

    const companyId = getCompanyContext();
    if (!companyId) {
      alert('Company ID not available. Please refresh and try again.');
      return;
    }

    if (!db) {
      alert('Database connection not available. Please check your internet connection and try again.');
      return;
    }

    try {
      setProcessingInvoices(prev => new Set(prev).add(reservation.id));

      const rentalAmount = parseFloat(reservation.rentalAmount) || 0;
      const addonCosts = parseFloat(reservation.addonCosts) || 0;
      const discountAmount = parseFloat(reservation.discountAmount) || 0;
      const deliveryCharges = parseFloat(reservation.deliveryCharges) || 0;
      const subtotalAmount = rentalAmount + addonCosts + deliveryCharges;
      const totalAmount = Math.max(0, subtotalAmount - discountAmount);

      const confirmed = window.confirm(
        `Create invoice for ${reservation.customerName}?\n\n` +
        `Booking #: ${reservation.reservationNumber || 'N/A'}\n` +
        `Bike: ${reservation.motorbikeNumber}${reservation.bikeModel ? ` (${reservation.bikeModel})` : ''}\n` +
        `Rental: $${rentalAmount.toFixed(2)}\n` +
        `Add-ons: $${addonCosts.toFixed(2)}\n` +
        `Delivery: $${deliveryCharges.toFixed(2)}\n` +
        `Discount: $${discountAmount.toFixed(2)}\n` +
        `Total: $${totalAmount.toFixed(2)}\n\n` +
        `This will create an invoice using the SHARED invoice numbering system.\n` +
        `The invoice will appear in BOTH Invoice Management AND Finance Manager.`
      );

      if (!confirmed) {
        return;
      }

      console.log('🧾 Creating invoice using SHARED service for reservation:', reservation.id);
      
      const createdInvoice = await createInvoiceFromReservation(
        reservation, 
        currentUser, 
        currentCompany,
        companyId
      );
      
      console.log('✅ Invoice created successfully:', createdInvoice.invoiceNumber);
      console.log('✅ Invoice is now available in Invoice Management AND Finance Manager');
      
      setCreatedInvoices(prev => {
        const newSet = new Set(prev).add(reservation.id);
        updateStoredInvoices(newSet);
        return newSet;
      });
      
      if (onInvoiceCreated) {
        onInvoiceCreated(createdInvoice);
      }

      const showPreview = window.confirm(
        `✅ Invoice created successfully!\n\n` +
        `Booking #: ${reservation.reservationNumber || 'N/A'}\n` +
        `Invoice Number: ${createdInvoice.invoiceNumber}\n` +
        `Total: $${totalAmount.toFixed(2)}\n\n` +
        `✅ The invoice is now available in:\n` +
        `   • Invoice Management\n` +
        `   • Finance Manager (as income)\n\n` +
        `Would you like to preview the invoice now?`
      );

      if (showPreview) {
        await handleInvoicePreview(reservation, createdInvoice);
      }

    } catch (error) {
      console.error('❌ Invoice creation error:', error);
      
      if (error.message.includes('already exists') || error.message.includes('Duplicate')) {
        setCreatedInvoices(prev => {
          const newSet = new Set(prev).add(reservation.id);
          updateStoredInvoices(newSet);
          return newSet;
        });
        alert(`An invoice already exists for this reservation.\n\nPlease check Invoice Management to view the existing invoice.`);
      } else if (error.message.includes('Firebase not available')) {
        alert(`Database connection error.\n\n${error.message}\n\nPlease check your internet connection and try again.`);
      } else {
        alert(`Failed to create invoice:\n\n${error.message}`);
      }
    } finally {
      setProcessingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(reservation.id);
        return newSet;
      });
    }
  };

  const handleInvoicePreview = async (reservation, createdInvoice = null) => {
    console.log('📄 Navigating to shared invoice preview page for reservation:', reservation.id);
    
    if (!onNavigateToInvoicePreview) {
      console.warn('onNavigateToInvoicePreview function not provided');
      alert('Navigation to invoice preview not configured. Please set up the navigation handler.');
      return;
    }

    const hasInvoiceForThisReservation = createdInvoices.has(reservation.id);
    let invoiceData = createdInvoice;

    if (!invoiceData && hasInvoiceForThisReservation) {
      try {
        const companyId = getCompanyContext?.();
        if (!companyId || !db) {
          console.warn('Cannot fetch invoice: Company context or database not available');
          alert('Unable to load invoice preview. Database connection required.');
          return;
        }

        console.log('📄 Fetching actual invoice from SHARED collection for reservation:', reservation.id);
        
        const loadingToast = setTimeout(() => {
          console.log('Still loading invoice...');
        }, 1000);

        invoiceData = await fetchInvoiceForReservation(reservation.id, companyId);
        clearTimeout(loadingToast);
        
        if (invoiceData) {
          console.log('✅ Found invoice from SHARED collection:', invoiceData.invoiceNumber);
        } else {
          console.warn('⚠️ Invoice marked as created but not found in database');
          alert('Invoice not found in database. It may have been deleted or there was an error.');
          return;
        }
      } catch (error) {
        console.error('❌ Error fetching invoice:', error);
        alert(`Failed to load invoice data:\n\n${error.message}\n\nPlease try again.`);
        return;
      }
    }
    
    const invoiceForPreview = invoiceData ? {
      ...invoiceData,
      customerName: invoiceData.customerName || reservation.customerName,
      email: invoiceData.customerEmail || reservation.email,
      customerEmail: invoiceData.customerEmail || reservation.email,
      whatsappNumber: invoiceData.customerPhone || reservation.whatsappNumber,
      customerPhone: invoiceData.customerPhone || reservation.whatsappNumber,
      motorbikeNumber: invoiceData.motorbikeNumber || reservation.motorbikeNumber,
      bikeModel: invoiceData.bikeModel || reservation.bikeModel,
      reservationNumber: invoiceData.reservationNumber || reservation.reservationNumber,
      pickupDateTime: invoiceData.pickupDateTime || reservation.pickupDateTime,
      dropoffDateTime: invoiceData.dropoffDateTime || reservation.dropoffDateTime,
      rentalPaymentStatus: invoiceData.status || 'generated',
      invoiceNumber: invoiceData.invoiceNumber,
      deliveryCharges: invoiceData.deliveryCharges || reservation.deliveryCharges || 0,
      pickupLocation: invoiceData.pickupLocation || reservation.pickupLocation,
      dropoffLocation: invoiceData.dropoffLocation || reservation.dropoffLocation,
      pickupLocationDetails: invoiceData.pickupLocationDetails || reservation.pickupLocationDetails,
      dropoffLocationDetails: invoiceData.dropoffLocationDetails || reservation.dropoffLocationDetails,
    } : {
      id: reservation.id,
      reservationId: reservation.id,
      reservationNumber: reservation.reservationNumber || '',
      customerName: reservation.customerName,
      email: reservation.email,
      customerEmail: reservation.email,
      whatsappNumber: reservation.whatsappNumber,
      customerPhone: reservation.whatsappNumber,
      motorbikeNumber: reservation.motorbikeNumber,
      bikeModel: reservation.bikeModel,
      totalAmount: reservation.totalAmount || (
        (parseFloat(reservation.rentalAmount) || 0) + 
        (parseFloat(reservation.addonCosts) || 0) + 
        (parseFloat(reservation.deliveryCharges) || 0) -
        (parseFloat(reservation.discountAmount) || 0)
      ),
      rentalAmount: reservation.rentalAmount,
      deliveryCharges: reservation.deliveryCharges || 0,
      pickupLocation: reservation.pickupLocation || 'Not specified',
      dropoffLocation: reservation.dropoffLocation || 'Not specified',
      pickupLocationDetails: reservation.pickupLocationDetails || null,
      dropoffLocationDetails: reservation.dropoffLocationDetails || null,
      selectedAddons: reservation.selectedAddons || [],
      pickupDateTime: reservation.pickupDateTime,
      dropoffDateTime: reservation.dropoffDateTime,
      status: reservation.rentalPaymentStatus || 'generated',
      rentalPaymentStatus: reservation.rentalPaymentStatus,
      paymentMethod: reservation.paymentMethod,
      licenseNumber: reservation.licenseNumber,
      note: reservation.note,
      depositAmount: reservation.depositAmount,
      discountData: reservation.discountData,
      hasDiscount: (parseFloat(reservation.discountAmount) || 0) > 0,
      createdAt: reservation.createdAt || new Date().toISOString(),
      invoiceNumber: 'PREVIEW-ONLY',
      isExtendedRental: false,
      automaticallyGenerated: false,
      type: 'regular',
      isPreviewOnly: true
    };
    
    onNavigateToInvoicePreview({
      invoice: invoiceForPreview,
      sourceComponent: 'reservations',
      currentUser,
      currentCompany
    });
  };

  if (!reservations || reservations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-4 sm:p-6 md:p-8 text-gray-400" style={{ minHeight: '300px' }}>
        <AlertCircle size={40} className="mb-3 sm:mb-4 text-gray-500 sm:w-12 sm:h-12" />
        <p className="text-sm sm:text-base md:text-lg font-medium text-center">{emptyMessage}</p>
        <p className="text-xs sm:text-sm mt-2 text-gray-500 text-center">Select a different date to view reservations</p>
      </div>
    );
  }

  const formatDate = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (error) {
      return dateTimeString;
    }
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  };

  const hasAddons = (reservation) => {
    return reservation.selectedAddons && 
           Array.isArray(reservation.selectedAddons) && 
           reservation.selectedAddons.length > 0;
  };

  const getAddonCost = (reservation) => {
    return parseFloat(reservation.addonCosts) || 0;
  };

  const getDiscountAmount = (reservation) => {
    return parseFloat(reservation.discountAmount) || 0;
  };

  const getDeliveryCharges = (reservation) => {
    return parseFloat(reservation.deliveryCharges) || 0;
  };

  const getTotalAmount = (reservation) => {
    if (reservation.totalAmount) {
      return parseFloat(reservation.totalAmount);
    }
    const rentalAmount = parseFloat(reservation.rentalAmount) || 0;
    const addonAmount = getAddonCost(reservation);
    const deliveryAmount = getDeliveryCharges(reservation);
    const discountAmount = getDiscountAmount(reservation);
    return Math.max(0, rentalAmount + addonAmount + deliveryAmount - discountAmount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'booked': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'pending': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'cancel': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // Sort reservations to show newest first
  const sortedReservations = [...reservations].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    
    if (dateA && dateB) {
      return dateB - dateA;
    }
    
    const pickupA = a.pickupDateTime ? new Date(a.pickupDateTime).getTime() : 0;
    const pickupB = b.pickupDateTime ? new Date(b.pickupDateTime).getTime() : 0;
    
    return pickupB - pickupA;
  });

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: scrollbarCSS }} />
      
      <div 
        className="reservation-scrollable space-y-2 sm:space-y-3 bg-gray-900/50 p-2 sm:p-3 md:p-4 rounded-lg border border-gray-700/50"
        style={scrollableContainerStyle}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-2 sm:pb-3 border-b border-gray-700/50 gap-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex-shrink-0"></div>
            <span className="text-white font-medium text-xs sm:text-sm">Reservations</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 text-xs bg-gray-800 px-2 py-1 rounded-full">
              {reservations.length} total
            </span>
            {createdInvoices.size > 0 && (
              <span className="text-green-400 text-xs bg-green-900/30 px-2 py-1 rounded-full border border-green-500/30">
                ✓ {createdInvoices.size} invoiced
              </span>
            )}
          </div>
        </div>
        
        {/* Reservation Cards */}
        {sortedReservations.map((reservation, index) => {
          const isProcessing = processingInvoices.has(reservation.id);
          const hasInvoice = createdInvoices.has(reservation.id);
          const totalAmount = getTotalAmount(reservation);
          const discountAmount = getDiscountAmount(reservation);
          const deliveryAmount = getDeliveryCharges(reservation);
          // ✅ NEW: documents for this reservation
          const uploadedDocs = getUploadedDocuments(reservation);
          const missingRequiredDocs = getMissingRequiredDocuments(reservation);
          
          return (
            <div
              key={reservation.id || index}
              className={`relative bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3 sm:p-4 ${hoverColor} transition-all duration-200 ${
                hasInvoice ? '' : 'cursor-pointer hover:shadow-lg hover:shadow-orange-500/10'
              } ${
                reservation.availabilityStatus === 'cancel' ? 'opacity-60' : ''
              }`}
              onClick={() => {
                if (!hasInvoice && onExpandDetails) {
                  onExpandDetails(reservation);
                }
              }}
            >
              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4 items-start">
                
                {/* Customer Info Section */}
                <div className="lg:col-span-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className={`w-10 h-10 ${reservation.availabilityStatus === 'cancel' ? 'bg-red-500/20' : 'bg-green-500/20'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <User className={`w-5 h-5 ${reservation.availabilityStatus === 'cancel' ? 'text-red-400' : 'text-green-400'}`} />
                      </div>
                      <div>
                        <h4 className="text-white font-medium text-sm font-mono">
                          {reservation.customerName || "Unknown"}
                        </h4>
                        <p className="text-xs text-gray-400">
                          {formatDate(reservation.createdAt || reservation.pickupDateTime)}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2 flex-shrink-0">#{index + 1}</span>
                  </div>
                  
                  {/* Reservation Number Badge */}
                  {reservation.reservationNumber && (
                    <div className="mb-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/40 rounded-lg px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0 flex-1">
                          <span className="text-xs text-orange-300/80 font-medium">Booking #</span>
                          <span className="text-sm font-bold text-orange-400 tracking-wide truncate">
                            {reservation.reservationNumber}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Contact Info */}
                  <div className="space-y-1">
                    {reservation.whatsappNumber && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <Phone className="w-3 h-3 text-green-400 flex-shrink-0" />
                        <span className="truncate">{reservation.whatsappNumber}</span>
                      </div>
                    )}
                    {reservation.email && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <Mail className="w-3 h-3 text-blue-400 flex-shrink-0" />
                        <span className="truncate">{reservation.email}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Date & Bike Info Section */}
                <div className="lg:col-span-4">
                  <div className="bg-gray-700/30 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-3 h-3 text-orange-400 flex-shrink-0" />
                      <span className="text-xs font-medium text-gray-200 truncate">
                        {formatDate(reservation.pickupDateTime)} - {formatDate(reservation.dropoffDateTime)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3 text-orange-400 flex-shrink-0" />
                      <span className="text-xs text-gray-300 truncate">
                        {formatTime(reservation.pickupDateTime)} - {formatTime(reservation.dropoffDateTime)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Bike Info */}
                  <div className="space-y-2">
                    {reservation.motorbikeNumber && (
                      <div className="bg-gray-800/50 rounded-lg p-2 border border-gray-700/50">
                        <div className="flex items-center gap-2 mb-1">
                          <MotorbikeIcon className="text-orange-400 flex-shrink-0" size={14} />
                          <span className="text-xs font-medium text-white truncate">
                            {reservation.motorbikeNumber}
                          </span>
                        </div>
                        {reservation.bikeModel && reservation.bikeModel !== 'Unknown' && (
                          <div className="flex items-center gap-2 pl-5">
                            <span className="text-xs text-gray-400">
                              Model: <span className="text-orange-300 font-medium">{reservation.bikeModel}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {reservation.licenseNumber && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate">License: {reservation.licenseNumber}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Amount & Actions Section */}
                <div className="lg:col-span-4">
                  <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-green-300 font-medium">Total Amount</span>
                      <DollarSign className="w-3 h-3 text-green-400 flex-shrink-0" />
                    </div>
                    <div className="text-base sm:text-lg font-bold text-green-400">
                      ${totalAmount.toFixed(2)}
                    </div>
                    
                    <div className="mt-2 space-y-1 text-xs">
                      {reservation.rentalAmount && (
                        <div className="flex justify-between text-gray-300">
                          <span>Rental:</span>
                          <span>${parseFloat(reservation.rentalAmount).toFixed(2)}</span>
                        </div>
                      )}
                      {hasAddons(reservation) && getAddonCost(reservation) > 0 && (
                        <div className="flex justify-between text-purple-300">
                          <span>Add-ons:</span>
                          <span>${getAddonCost(reservation).toFixed(2)}</span>
                        </div>
                      )}
                      {deliveryAmount > 0 && (
                        <div className="flex justify-between text-purple-300">
                          <span>Delivery:</span>
                          <span>${deliveryAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {discountAmount > 0 && (
                        <div className="flex justify-between text-red-300">
                          <span>Discount:</span>
                          <span>-${discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    {showInvoiceButton && (reservation.availabilityStatus === 'booked' || reservation.availabilityStatus === 'completed') && (
                      <button
                        className={`flex items-center justify-center gap-1 sm:gap-2 text-white px-3 py-2 rounded-lg text-xs transition-all shadow-md flex-1 ${
                          isProcessing
                            ? 'bg-gray-600 cursor-not-allowed opacity-75'
                            : (hasInvoice || reservation.availabilityStatus === 'completed')
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:scale-95'
                            : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 active:scale-95'
                        }`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (hasInvoice || reservation.availabilityStatus === 'completed') {
                            await handleInvoicePreview(reservation);
                          } else {
                            handleCreateInvoice(reservation);
                          }
                        }}
                        disabled={isProcessing}
                        title={
                          isProcessing 
                            ? 'Creating invoice...' 
                            : (hasInvoice || reservation.availabilityStatus === 'completed')
                            ? 'View Invoice Preview' 
                            : 'Create Invoice (With Delivery Charges)'
                        }
                      >
                        {isProcessing ? (
                          <>
                            <Loader size={12} className="animate-spin flex-shrink-0" />
                            <span className="hidden sm:inline">Creating...</span>
                          </>
                        ) : (hasInvoice || reservation.availabilityStatus === 'completed') ? (
                          <>
                            <Receipt size={12} className="flex-shrink-0" /> 
                            Preview
                          </>
                        ) : (
                          <>
                            <Receipt size={12} className="flex-shrink-0" /> 
                            <span className="hidden sm:inline">Create Invoice</span>
                            <span className="sm:hidden">Create</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Status Messages */}
                  {reservation.availabilityStatus === 'pending' && (
                    <div className="w-full text-center text-xs text-amber-400 bg-amber-900/20 py-1.5 sm:py-2 rounded-lg border border-amber-700/50 mt-2">
                      Pending - Invoice available after booking
                    </div>
                  )}
                  
                  {reservation.availabilityStatus === 'completed' && (
                    <div className="w-full text-center text-xs text-blue-400 bg-blue-900/20 py-1.5 sm:py-2 rounded-lg border border-blue-700/50 mt-2">
                      Completed - Rental finished
                    </div>
                  )}
                  
                  {reservation.availabilityStatus === 'cancel' && (
                    <div className="w-full text-center text-xs text-red-400 bg-red-900/20 py-1.5 sm:py-2 rounded-lg border border-red-700/50 mt-2">
                      Cancelled
                    </div>
                  )}
                </div>
              </div>

              {/* ✅ NEW: Documents Section - quick access links */}
              {(uploadedDocs.length > 0 || missingRequiredDocs.length > 0) && (
                <div className="mt-3 pt-3 border-t border-gray-700/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-cyan-300">
                      Documents {uploadedDocs.length > 0 && `(${uploadedDocs.length})`}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uploadedDocs.map((document) => (
                      <a
                        key={document.id}
                        href={document.downloadURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        // Stop the click from also opening the edit form
                        onClick={(e) => e.stopPropagation()}
                        title={`Open ${document.name}${document.fileName ? ` (${document.fileName})` : ''}`}
                        className="flex items-center gap-1.5 bg-cyan-500/10 hover:bg-cyan-500/25 border border-cyan-500/30 hover:border-cyan-400 rounded-lg px-2.5 py-1.5 text-xs text-cyan-200 transition-colors max-w-full"
                      >
                        {isImageDocument(document) ? (
                          <ImageIcon className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                        ) : (
                          <FileText className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                        )}
                        <span className="truncate">{document.name}</span>
                        <ExternalLink className="w-3 h-3 text-cyan-500 flex-shrink-0" />
                      </a>
                    ))}

                    {missingRequiredDocs.map((req) => (
                      <span
                        key={`missing-${req.id}`}
                        title="Required document not uploaded"
                        className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 rounded-lg px-2.5 py-1.5 text-xs text-red-300"
                      >
                        <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                        <span className="truncate">{req.name} missing</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add-ons Section */}
              {hasAddons(reservation) && (
                <div className="mt-3 pt-3 border-t border-gray-700/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-3 h-3 text-purple-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-purple-300">Add-ons Selected</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {reservation.selectedAddons.slice(0, 3).map((addon, addonIndex) => (
                      <div key={addonIndex} className="flex items-center justify-between bg-purple-500/10 border border-purple-500/20 rounded px-2 py-1">
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          <Package className="w-3 h-3 text-purple-400 flex-shrink-0" />
                          <span className="text-xs text-white truncate">{addon.name}</span>
                        </div>
                        <span className="text-xs text-purple-400 font-medium ml-1 flex-shrink-0">
                          ${addon.calculatedCost || addon.price}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Company Info */}
              {showCompanyInfo && reservation.companyName && (
                <div className="mt-3 pt-3 border-t border-gray-700/50">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Shield className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">Company: {reservation.companyName}</span>
                  </div>
                </div>
              )}
              
              {/* Cancellation Info */}
              {reservation.availabilityStatus === 'cancel' && (
                <div className="mt-3 pt-3 border-t border-red-700/50">
                  <div className="flex items-center gap-2 text-xs text-red-400">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">
                      Cancelled {reservation.cancelledAt && `on ${new Date(reservation.cancelledAt).toLocaleDateString()}`}
                      {reservation.cancelledByUsername && ` by ${reservation.cancelledByUsername}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Footer */}
        {sortedReservations.length > 5 && (
          <div className="text-center py-2">
            <div className="text-xs text-gray-500 bg-gray-800/50 px-3 py-1 rounded-full inline-block border border-gray-700/50">
              Showing all {sortedReservations.length} reservations
            </div>
          </div>
        )}
      </div>

    </>
  );
};

export default ReservationList;